import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './lib/AuthContext';
import { WorkflowProvider } from './lib/WorkflowContext';
import { Toaster } from './components/ui/sonner';
import { CommandPalette } from './components/CommandPalette';
import AppShell from './components/AppShell';
import LoginPage from './pages/LoginPage';
import './App.css';

// Lazy load pages for better performance
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const StudioCatalog = lazy(() => import('./pages/StudioCatalog'));
const AgentDetail = lazy(() => import('./pages/AgentDetail'));
const ConsoleDashboard = lazy(() => import('./pages/ConsoleDashboard'));
const EscalationQueue = lazy(() => import('./pages/EscalationQueue'));
const ActivityLog = lazy(() => import('./pages/ActivityLog'));
const MetricsPage = lazy(() => import('./pages/MetricsPage'));
const PortalDashboard = lazy(() => import('./pages/PortalDashboard'));
const ReportLibrary = lazy(() => import('./pages/ReportLibrary'));
const ReportViewer = lazy(() => import('./pages/ReportViewer'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const OrchestrationPage = lazy(() => import('./pages/OrchestrationPage'));
const OrgChartPage = lazy(() => import('./pages/OrgChartPage'));
const TeamManagerPage = lazy(() => import('./pages/TeamManagerPage'));
const CommsTheaterPage = lazy(() => import('./pages/CommsTheaterPage'));
const CommsInboxPage = lazy(() => import('./pages/CommsInboxPage'));
const CfoCommandCenter = lazy(() => import('./pages/CfoCommandCenter'));
const CfoBriefingsPage = lazy(() => import('./pages/CfoBriefingsPage'));
const CfoDirectivesPage = lazy(() => import('./pages/CfoDirectivesPage'));
const AutonomousControlPage = lazy(() => import('./pages/AutonomousControlPage'));
const CommsNetworkPage = lazy(() => import('./pages/CommsNetworkPage'));
const MarketplacePage = lazy(() => import('./pages/MarketplacePage'));
const SimulationPage = lazy(() => import('./pages/SimulationPage'));
const IntegrationHubPage = lazy(() => import('./pages/IntegrationHubPage'));
const ToolStudioPage = lazy(() => import('./pages/ToolStudioPage'));
const AgentFactoryPage = lazy(() => import('./pages/AgentFactoryPage'));
const GatewayPage = lazy(() => import('./pages/GatewayPage'));
const ActivationPage = lazy(() => import('./pages/ActivationPage'));
const SalesOrderDashboard = lazy(() => import('./pages/SalesOrderDashboard'));
const AgentWorkflowDashboard = lazy(() => import('./pages/AgentWorkflowDashboard'));

// Loading fallback component
const PageLoader = () => (
  <div className="flex items-center justify-center h-64">
    <div className="animate-spin w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full"></div>
  </div>
);

function ProtectedRoute({ children }) {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen bg-slate-50"><div className="animate-pulse text-slate-400 font-display text-xl">Loading Cygnus...</div></div>;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Suspense fallback={<PageLoader />}>{children}</Suspense>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
      <Route path="/" element={<ProtectedRoute><AppShell /></ProtectedRoute>}>
        <Route index element={<Navigate to="/console" replace />} />
        <Route path="studio" element={<StudioCatalog />} />
        <Route path="studio/agents/:agentId" element={<AgentDetail />} />
        <Route path="studio/org" element={<OrgChartPage />} />
        <Route path="studio/org/teams" element={<TeamManagerPage />} />
        <Route path="studio/autonomous" element={<AutonomousControlPage />} />
        <Route path="studio/marketplace" element={<MarketplacePage />} />
        <Route path="studio/factory" element={<AgentFactoryPage />} />
        <Route path="integrations" element={<IntegrationHubPage />} />
        <Route path="tools" element={<ToolStudioPage />} />
        <Route path="gateway" element={<GatewayPage />} />
        <Route path="activate" element={<ActivationPage />} />
        <Route path="sales-orders" element={<SalesOrderDashboard />} />
        <Route path="workflow" element={<AgentWorkflowDashboard />} />
        <Route path="comms" element={<CommsTheaterPage />} />
        <Route path="comms/inbox" element={<CommsInboxPage />} />
        <Route path="comms/network" element={<CommsNetworkPage />} />
        <Route path="cfo" element={<CfoCommandCenter />} />
        <Route path="cfo/briefings" element={<CfoBriefingsPage />} />
        <Route path="cfo/directives" element={<CfoDirectivesPage />} />
        <Route path="simulation" element={<SimulationPage />} />
        <Route path="console" element={<ConsoleDashboard />} />
        <Route path="console/escalations" element={<EscalationQueue />} />
        <Route path="console/activity" element={<ActivityLog />} />
        <Route path="console/metrics" element={<MetricsPage />} />
        <Route path="console/audit" element={<AuditLogPage />} />
        <Route path="console/orchestration" element={<OrchestrationPage />} />
        <Route path="portal" element={<PortalDashboard />} />
        <Route path="portal/reports" element={<ReportLibrary />} />
        <Route path="portal/reports/:reportType" element={<ReportViewer />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <WorkflowProvider>
          <AppRoutes />
          <CommandPalette />
          <Toaster position="top-right" richColors />
        </WorkflowProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
